"use client";
import { Facebook, Linkedin, Youtube } from "lucide-react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useTransition } from "@/context/TransitionContext";

const FooterSection = ({ dipImg }: { dipImg: boolean }) => {
  const { triggerTransition } = useTransition();
  const pathname = usePathname();
  const router = useRouter();

  const handleRouteNavigation = (href: string) => {
    if (typeof window === "undefined") return;

    if (pathname === href) {
      window.location.assign(href);
      return;
    }

    triggerTransition(href);
  };

  return (
    <section id="contact" className="footer-section relative w-screen">
      {/* Canvas for 3D model - positioned as background */}

      {/* HTML content overlay */}
      <div className="relative z-10">

        {
          dipImg && (
            <Image
              src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472250/footer-dip_ja7mip.png"
              alt="Decoration"
              width={1440}
              height={320}
              className="w-full h-auto object-cover -translate-y-1"
            />
          )
        }

        <div className="relative md:pt-[20vh] pt-[10vh]">
          {/* Background image */}
          <div className="absolute inset-0 w-full h-full">
            <Image
              src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472267/footer_jv22by.png"
              alt="Kunafa Mafias footer background"
              fill
              className="object-contain"
              sizes="100vw"
            />
          </div>
          {/* Dark overlay on top of the image */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Heading above the darkened background */}
          <div className="relative flex items-center justify-center overflow-hidden z-[1000]">
            <button
              onClick={() => handleRouteNavigation("/")}
              className="general-title text-center text-milk py-5 cursor-pointer hover:scale-105 transition-transform duration-300"
            >
              #Kunafa Mafias
            </button>
          </div>

          <div className="flex-center gap-5 relative z-10 md:mt-20 mt-5">
            <a
              href="https://www.facebook.com/profile.php?id=61587488227109"
              target="_blank"
              rel="noreferrer"
              aria-label="Kunafa Mafias on Facebook"
              className="social-btn"
            >
              <Facebook className="size-6 text-[#FAEADE] fill-[#FAEADE]" />
            </a>
            <a
              href="https://www.instagram.com/kunafamafias"
              target="_blank"
              rel="noreferrer"
              aria-label="Kunafa Mafias on Instagram"
              className="social-btn"
            >
              <Image src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472227/insta_dy6ulw.svg" alt="Kunafa Mafias on Instagram" width={24} height={24} />
            </a>
            <a
              href="https://www.linkedin.com/company/kunafamafias"
              target="_blank"
              rel="noreferrer"
              aria-label="Kunafa Mafias on LinkedIn"
              className="social-btn"
            >
              <Linkedin className="size-6 text-[#FAEADE] fill-[#FAEADE]" />
            </a>
            <a
              href="https://youtube.com/@kunafamafias?si=qRzafd5z4ROmS9IK"
              target="_blank"
              rel="noreferrer"
              aria-label="Kunafa Mafias on YouTube"
              className="social-btn"
            >
              <Youtube className="size-6 text-[#FAEADE]" />
            </a>
          </div>

          <div className="relative z-10 mt-8 px-5 flex flex-col items-center gap-2 text-milk font-paragraph text-base md:text-lg">
            <a href="tel:+918090724107" className="hover:underline">
              +91 8090724107
            </a>
            <a href="mailto:kunafamafias@gmail.com" className="hover:underline">
              kunafamafias@gmail.com
            </a>
            <button
              type="button"
              onClick={() => handleRouteNavigation("/contact-us")}
              className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#FAEADE]/80 hover:text-[#FAEADE] transition-colors"
            >
              Contact Us
            </button>
          </div>

          {/* WhatsApp Button */}
          <div className="relative z-10 mt-8 flex justify-center">
            <button
              onClick={() => router.push("https://wa.me/919278365551")}
              className="group relative inline-flex items-center justify-center gap-3 bg-[#F9EADE] text-[#553424] w-64 h-16 rounded-full hover:bg-white transition-all duration-500 shadow-xl overflow-hidden cursor-pointer"
            >
              <span className="absolute left-2 flex items-center justify-center w-12 h-12 bg-[#553424] text-[#F9EADE] rounded-full transition-all duration-500 ease-in-out group-hover:left-[calc(100%-3.5rem)] group-hover:bg-[#553424] group-hover:text-[#F9EADE]">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="w-6 h-6">
                  <path fill="currentColor" d="M476.9 161.1C435 119.1 379.2 96 319.9 96C197.5 96 97.9 195.6 97.9 318C97.9 357.1 108.1 395.3 127.5 429L96 544L213.7 513.1C246.1 530.8 282.6 540.1 319.8 540.1L319.9 540.1C442.2 540.1 544 440.5 544 318.1C544 258.8 518.8 203.1 476.9 161.1zM319.9 502.7C286.7 502.7 254.2 493.8 225.9 477L219.2 473L149.4 491.3L168 423.2L163.6 416.2C145.1 386.8 135.4 352.9 135.4 318C135.4 216.3 218.2 133.5 320 133.5C369.3 133.5 415.6 152.7 450.4 187.6C485.2 222.5 506.6 268.8 506.5 318.1C506.5 419.9 421.6 502.7 319.9 502.7zM421.1 364.5C415.6 361.7 388.3 348.3 383.2 346.5C378.1 344.6 374.4 343.7 370.7 349.3C367 354.9 356.4 367.3 353.1 371.1C349.9 374.8 346.6 375.3 341.1 372.5C308.5 356.2 287.1 343.4 265.6 306.5C259.9 296.7 271.3 297.4 281.9 276.2C283.7 272.5 282.8 269.3 281.4 266.5C280 263.7 268.9 236.4 264.3 225.3C259.8 214.5 255.2 216 251.8 215.8C248.6 215.6 244.9 215.6 241.2 215.6C237.5 215.6 231.5 217 226.4 222.5C221.3 228.1 207 241.5 207 268.8C207 296.1 226.9 322.5 229.6 326.2C232.4 329.9 268.7 385.9 324.4 410C359.6 425.2 373.4 426.5 391 423.9C401.7 422.3 423.8 410.5 428.4 397.5C433 384.5 433 373.4 431.6 371.1C430.3 368.6 426.6 367.2 421.1 364.5z" />
                </svg>
              </span>
              <span className="font-bold uppercase tracking-wider text-base md:text-lg transition-transform duration-500 ease-in-out translate-x-6 group-hover:-translate-x-6">
                Chat on WhatsApp
              </span>
            </button>
          </div>

          <div className="copyright-box">
            <p>Copyright © 2025 Kunafa Mafias - All Rights Reserved</p>
            <div className="flex items-center gap-7">
              <p>Privacy Policy</p>
              <p>Terms of Sеrvice</p>
            </div>
          </div>
        </div>
      </div>
      {/* <Canvas className="absolute bottom-100 w-full h-[70%] z-100">
        <KunafaModel />
      </Canvas> */}
    </section>
  );
};

export default FooterSection;
